import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { ChevronRight, Check, Palette, Package, Award, Truck } from "lucide-react";

import bolPersonnalise from "@/assets/products/bol-personnalise.png";
import sacPersonnalise from "@/assets/products/sac-personnalise-ams.png";
import sacPersonnaliseDuo from "@/assets/products/sac-personnalise-ams-duo.png";
import gobeletBlanc from "@/assets/products/gobelet-blanc-200ml.jpg";
import serviettes from "@/assets/products/serviettes-blanches.png";


interface CustomizableProduct {
  id: string;
  name: string;
  description: string;
  image: string;
  minOrder: string;
  maxColors: number;
  bestPrice: string;
  features: string[];
}

const products: CustomizableProduct[] = [
  {
    id: "bol",
    name: "Bol personnalisé",
    description: "Saladiers ronds kraft avec impression de votre logo en flexographie jusqu'à 4 couleurs",
    image: bolPersonnalise,
    minOrder: "20 000 pièces",
    maxColors: 4,
    bestPrice: "100 000 pièces",
    features: ["Impression flexo", "Kraft ou blanc", "Couvercle rPET inclus", "5 tailles disponibles"],
  },
  {
    id: "sac",
    name: "Sac kraft personnalisé",
    description: "Sacs kraft à poignées torsadées ou plates avec impression de votre logo",
    image: sacPersonnalise,
    minOrder: "5 000 pièces",
    maxColors: 4,
    bestPrice: "50 000 pièces",
    features: ["Poignées torsadées ou plates", "Plusieurs tailles", "Kraft naturel", "Impression recto/verso"],
  },
  {
    id: "gobelet",
    name: "Gobelet personnalisé",
    description: "Gobelets carton kraft ou blanc avec impression intégrale de votre identité visuelle",
    image: gobeletBlanc,
    minOrder: "50 000 pièces",
    maxColors: 4,
    bestPrice: "100 000 pièces",
    features: ["Impression intégrale", "Kraft ou blanc", "4 contenances", "Couvercle disponible"],
  },
  {
    id: "serviette",
    name: "Serviette personnalisée",
    description: "Serviettes en papier 2 plis avec impression de votre logo en une couleur",
    image: serviettes,
    minOrder: "100 000 pièces",
    maxColors: 1,
    bestPrice: "100 000 pièces",
    features: ["Impression 1 couleur", "Papier 2 plis", "Format 25x32 cm", "Douce et absorbante"],
  },
];


const Personnalisation = () => {
  const [selectedProduct, setSelectedProduct] = useState(0);
  const current = products[selectedProduct];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Personnalisation sur mesure - Emballages avec votre logo | AMS DISTRIB"
        description="Personnalisez vos emballages alimentaires avec votre logo : bols, sacs kraft, gobelets et serviettes. Impression flexo jusqu'à 4 couleurs. Devis gratuit."
      />
      <Header />
      <main>
        {/* Breadcrumb */}
        <section className="pt-24 pb-4">
          <div className="container mx-auto px-4">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/produits" className="hover:text-primary transition-colors">Produits</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-foreground font-medium">Personnalisation</span>
            </nav>
          </div>
        </section>

        {/* Hero: Image + Info */}
        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
              {/* Left: Product Image */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-background border border-border rounded-2xl p-8 md:p-12 flex items-center justify-center aspect-square">
                  <motion.img
                    key={current.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    src={current.image}
                    alt={current.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>

                {/* Product Thumbnails */}
                <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                  {products.map((p, i) => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedProduct(i)}
                      className={`shrink-0 w-20 h-20 rounded-lg border-2 p-2 transition-all ${
                        i === selectedProduct
                          ? "border-primary bg-secondary/30"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-contain"
                      />
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Right: Product Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-primary/15 to-primary/5 text-primary text-xs rounded-full font-bold border border-primary/20">
                    <Palette className="w-3.5 h-3.5" />
                    Sur mesure
                  </span>
                </div>

                <motion.h1
                  key={current.id + "-title"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="font-display text-3xl md:text-4xl font-bold text-accent mb-3"
                >
                  {current.name}
                </motion.h1>
                <motion.p
                  key={current.id + "-desc"}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="text-muted-foreground text-lg mb-6"
                >
                  {current.description}
                </motion.p>

                {/* Features Checklist */}
                <div className="space-y-3 mb-8">
                  {current.features.map((feature, i) => (
                    <motion.div
                      key={`${current.id}-${i}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 + 0.2 }}
                      className="flex items-center gap-3"
                    >
                      <div className="w-6 h-6 rounded-full bg-eco/20 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4 text-eco" />
                      </div>
                      <span className="text-foreground">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                <div className="space-y-3">
                  <Button variant="kraft" size="lg" asChild className="w-full md:w-auto">
                    <Link to="/devis">Demander un devis personnalisé</Link>
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Specs Table */}
        <section className="py-12 border-t border-border">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-2xl font-bold text-accent mb-8">
              Détails de personnalisation
            </h2>

            {/* Product Selector Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {products.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedProduct(i)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    i === selectedProduct
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-foreground hover:bg-secondary/80"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>

            {/* Specs Table */}
            <div className="bg-card rounded-xl border border-border overflow-hidden max-w-2xl">
              <table className="w-full">
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium w-1/3">Produit</td>
                    <td className="px-6 py-4 text-foreground font-medium">{current.name}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Technique d'impression</td>
                    <td className="px-6 py-4 text-foreground font-medium">Flexographie</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Nombre de couleurs</td>
                    <td className="px-6 py-4 text-foreground font-medium">
                      <div className="flex items-center gap-2">
                        Jusqu'à {current.maxColors} couleur{current.maxColors > 1 ? "s" : ""}
                        <div className="flex gap-1 ml-2">
                          {Array.from({ length: current.maxColors }, (_, i) => {
                            const colors = ["bg-blue-500", "bg-red-500", "bg-amber-500", "bg-emerald-500"];
                            return <div key={i} className={`w-4 h-4 rounded-sm ${colors[i]}`} />;
                          })}
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Minimum de fabrication</td>
                    <td className="px-6 py-4 text-foreground font-medium">{current.minOrder}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Meilleur prix à partir de</td>
                    <td className="px-6 py-4 text-foreground font-medium">{current.bestPrice}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Délai de fabrication</td>
                    <td className="px-6 py-4 text-foreground font-medium">4 à 6 semaines</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Fichier requis</td>
                    <td className="px-6 py-4 text-foreground font-medium">Logo vectoriel (AI, PDF, SVG)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>


        {/* Process */}
        <section className="py-12 border-t border-border">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-2xl font-bold text-accent mb-10 text-center">
              Comment ça marche ?
            </h2>
            <div className="grid md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {[
                { step: "01", title: "Envoyez votre logo", desc: "Transmettez votre fichier vectoriel via notre formulaire de devis" },
                { step: "02", title: "Recevez votre devis", desc: "Notre équipe vous envoie un devis détaillé sous 24h" },
                { step: "03", title: "BAT & production", desc: "Validation du bon à tirer puis lancement en fabrication" },
                { step: "04", title: "Livraison", desc: "Réception sous 4 à 6 semaines, livraison France entière" },
              ].map((item, i) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center"
                >
                  <span className="font-display text-4xl font-bold text-primary/20 block mb-3">{item.step}</span>
                  <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Nos avantages */}
        <section className="py-12 border-t border-border bg-secondary/20">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-2xl font-bold text-accent mb-8 text-center">
              Nos avantages
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-medium text-foreground mb-2">Qualité certifiée</h3>
                <p className="text-sm text-muted-foreground">
                  Produits certifiés FSC, conformes aux normes européennes
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-eco/10 flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-eco" />
                </div>
                <h3 className="font-medium text-foreground mb-2">Accompagnement dédié</h3>
                <p className="text-sm text-muted-foreground">
                  Un interlocuteur unique du devis à la livraison
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-medium text-foreground mb-2">Livraison France</h3>
                <p className="text-sm text-muted-foreground">
                  Livraison sur toute la France métropolitaine
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Personnalisation;
